import React, { useEffect, useMemo, useState } from 'react';

import dayjs from 'dayjs';
import { Button, Card, Checkbox, Flex, Steps } from 'antd';
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { toast } from 'react-toastify';
import { useNavigate, useSearchParams } from 'react-router-dom';
import DashboardTemplate from 'src/templates/DashboardTemplate';
import { StepSubtitle } from 'src/pages/observers/AddObserverPage/styles';

import {
	useDeleteObserverWhatsappGroupMutation,
	useLazyGetObserverByIdQuery,
	useLazyGetObserverWhatsappGroupByIdQuery,
	usePatchObserverMutation,
	usePostObserverMutation,
	usePostObserverWhatsappGroupMutation
} from 'src/redux/services/centralApi';
import type {
	IQueryFieldsItem,
	ObserverReport,
	ReportBlock,
	ReportJoin,
	ReportQuery
} from 'src/interfaces/entities/observer';
import type { IQueryResult } from 'src/pages/observers/components/CodeEditor/types';
import {
	CodeEditor,
	FormItemInput,
	ObserverTypeAlert,
	PeriodicitySelect,
	PreviewMessageInput,
	QueryDisplay,
	ReportBlocksEditor,
	TagsSelect,
	WhatsAppGroupsSelect
} from 'src/pages/observers/components';
import { getJoinFields } from 'src/pages/observers/components/ReportBlocksEditor';

interface IQueryDraft {
	uid: string
	key: string
	sql: string
	fields: IQueryFieldsItem[]
	/** SQL que gerou os `fields`: editar o SQL exige executar de novo */
	testedSql: string
	result?: IQueryResult
}

const KEY_REGEX = /^[a-z_][a-z0-9_]*$/;

const newDraft = (index: number, query?: ReportQuery): IQueryDraft => ({
	uid: window.crypto.randomUUID(),
	key: query?.key ?? `consulta_${index}`,
	sql: query?.sql ?? '',
	fields: query?.fields ?? [],
	testedSql: query?.sql ?? '',
});

// key remonta o formulário ao trocar de grupo (ou de edição para criação): nada do anterior fica
export default function AddQueryGroupPage() {
	const [ searchParams, ] = useSearchParams();
	const id = searchParams.get('id');
	return <QueryGroupForm key={id ?? 'novo'} id={id} />;
}

const QueryGroupForm = ({ id, }: { id: string | null }) => {
	const navigate = useNavigate();

	const [ getObserverById, { data: observerData, },] = useLazyGetObserverByIdQuery();
	const [ getObserverWhatsappGroups, observerWhatsappGroupsResult, ] = useLazyGetObserverWhatsappGroupByIdQuery();
	const [ postObserver, postObserverResult, ] = usePostObserverMutation();
	const [ patchObserver, patchObserverResult, ] = usePatchObserverMutation();
	const [ postObserverWhatsappGroup, ] = usePostObserverWhatsappGroupMutation();
	const [ deleteObserverWhatsappGroup, ] = useDeleteObserverWhatsappGroupMutation();

	const [ currentStep, setCurrentStep, ] = useState<number>(0);

	const [ queries, setQueries, ] = useState<IQueryDraft[]>([ newDraft(1), ]);
	const [ joins, setJoins, ] = useState<ReportJoin[]>([]);
	const [ blocks, setBlocks, ] = useState<ReportBlock[]>([]);

	const [ name, setName, ] = useState<string>('');
	const [ shortName, setShortName, ] = useState<string>('');
	const [ tags, setTags, ] = useState<string[]>([]);
	const [ isActive, setIsActive, ] = useState<boolean>(true);
	const [ periodicity, setPeriodicity, ] = useState<string>();
	const [ customPreviewMessage, setCustomPreviewMessage, ] = useState<string>('');
	const [ whatsappGroups, setWhatsappGroups, ] = useState<string[]>([]);
	const [ defaultWhatsappGroups, setDefaultWhatsappGroups, ] = useState<string[]>([]);

	useEffect(() => {
		if (!id) return;
		getObserverById({ id, });
		getObserverWhatsappGroups({ id, });
	}, [ id, ]);

	useEffect(() => {
		if (!observerData) return;
		const report = observerData.report;
		setQueries(report?.queries?.length
			? report.queries.map((query, index) => newDraft(index + 1, query))
			: [ newDraft(1), ]);
		setJoins(report?.joins ?? []);
		setBlocks(report?.blocks ?? []);
		setName(observerData.name ?? '');
		setShortName(observerData.short_name ?? '');
		setTags(Array.isArray(observerData.tags) ? observerData.tags : []);
		setIsActive(observerData.is_active);
		setPeriodicity(observerData.periodicity_id || undefined);
		setCustomPreviewMessage(observerData.custom_preview_message ?? '');
	}, [ observerData, ]);

	useEffect(() => {
		const groupIds = observerWhatsappGroupsResult.data?.map((group) => group.group_id);
		if (!groupIds) return;
		setDefaultWhatsappGroups(groupIds);
		setWhatsappGroups(groupIds);
	}, [ observerWhatsappGroupsResult.data, ]);

	const sources = useMemo(() => queries.map(({ key, fields, }) => ({ key, fields, })), [ queries, ]);
	// amostra do teste de SQL de cada consulta, para o preview do relatório
	const previewData = useMemo(() => Object.fromEntries(queries
		.filter((query) => query.result?.data?.length)
		.map((query) => [ query.key, query.result?.data ?? [], ])), [ queries, ]);
	const messageSources = useMemo(() => [
		...sources,
		...joins.map((join) => ({ key: join.key, fields: getJoinFields(join, sources), })),
	], [ sources, joins, ]);

	const updateQuery = (uid: string, patch: (query: IQueryDraft) => Partial<IQueryDraft>) =>
		setQueries((prev) => prev.map((query) => (query.uid === uid ? { ...query, ...patch(query), } : query)));

	const queriesError = (): string | null => {
		if (queries.some((query) => !KEY_REGEX.test(query.key)))
			return 'Identificador inválido: use letras minúsculas, números e _ (sem começar com número).';
		if (new Set(queries.map((query) => query.key)).size !== queries.length)
			return 'Existem consultas com o mesmo identificador.';
		if (queries.some((query) => !query.fields.length || query.testedSql !== query.sql))
			return 'Execute todas as consultas (▶) com retorno válido para continuar.';
		return null;
	};

	const configError = (): string | null => (
		customPreviewMessage && name && shortName && periodicity
			? null
			: 'Preencha todos os campos marcados com \'*\' para salvar.'
	);

	const handleNextButton = () => {
		const error = queriesError() ?? (currentStep === 2 ? configError() : null);
		if (error) {
			toast.error(error);
			return;
		}
		if (currentStep < 2) {
			setCurrentStep((prev) => prev + 1);
			return;
		}
		handleSave();
	};

	const syncWhatsappGroups = (observerId: string) => {
		const current = observerWhatsappGroupsResult.data ?? [];
		current
			.filter((group) => !whatsappGroups.includes(group.group_id))
			.forEach((group) => deleteObserverWhatsappGroup({ observer_id: observerId, group_id: group.group_id, }));
		whatsappGroups
			.filter((groupId) => !current.some((group) => group.group_id === groupId))
			.forEach((groupId) => postObserverWhatsappGroup({ observer_id: observerId, group_id: groupId, }));
	};

	const handleError = (err: any) => {
		// 400 = validação (lista de { msg }); 500 = frontend_message
		toast.error(err?.data?.frontend_message ?? err?.data?.[0]?.msg ?? 'Ocorreu um erro inesperado!');
	};

	const handleSave = () => {
		const report: ObserverReport = {
			queries: queries.map(({ key, sql, fields, }) => ({ key, sql, fields, })),
			joins,
			blocks,
		};
		// Grupo de consultas não tem SQL único, severidade nem gatilho
		const observer = {
			name,
			tags,
			report,
			is_active: isActive,
			short_name: shortName,
			periodicity_id: periodicity ?? '',
			custom_preview_message: customPreviewMessage,
			flags: [ 'only_notify', ],
			sql_url: null,
			module_id: null,
			module_sql_url: null,
			severity_id: null,
			conditions: null,
			sql_test_result: [],
			row_restrictions: [],
			field_restrictions: [],
		};

		if (id) {
			patchObserver({ id, observer, })
				.unwrap()
				.then(() => {
					syncWhatsappGroups(id);
					toast.success('Grupo de consultas atualizado!', { autoClose: 1000, });
					navigate('/observador/listar');
				})
				.catch(handleError);
			return;
		}

		postObserver({ ...observer, creation_date: dayjs().format('YYYY-MM-DD'), })
			.unwrap()
			.then((res) => {
				syncWhatsappGroups(res.payload.observer.observer_id);
				navigate('/observador/listar');
			})
			.catch(handleError);
	};

	return <DashboardTemplate pageTitle={id ? 'Editar grupo de consultas' : 'Adicionar grupo de consultas'}>
		{!id && <ObserverTypeAlert current='group' />}
		<Steps
			direction='vertical'
			size='small'
			current={currentStep}
			items={[
				{
					title: 'Consultas SQL',
					description: <Flex vertical gap={16}>
						<StepSubtitle>
							Adicione as consultas que rodam juntas no mesmo horário e execute cada uma (▶).
						</StepSubtitle>
						<hr />
						{queries.map((query, index) => (
							<Card
								key={query.uid}
								size='small'
								title={`Consulta ${index + 1}`}
								extra={queries.length > 1 && <Button
									type='text'
									danger
									icon={<DeleteOutlined />}
									onClick={() => setQueries((prev) => prev.filter((item) => item.uid !== query.uid))}
								/>}
							>
								<Flex vertical gap={12}>
									<FormItemInput
										required
										value={query.key}
										label='Identificador:'
										placeholder='ex.: vendas'
										tooltip='Nome usado na mensagem ({{ queries.vendas.count }}), nos cruzamentos e no relatório. Letras minúsculas, números e _.'
										onInputChange={(value) => {
											if (typeof value === 'string')
												updateQuery(query.uid, () => ({
													key: value.toLowerCase().replace(/[^a-z0-9_]/g, '_'),
												}));
										}}
									/>
									<div>
										<CodeEditor
											editor_props={{ value: query.sql, }}
											executable
											height='260px'
											conditions={{}}
											hasReturn={Boolean(query.result)}
											onCodeChange={(sql) => updateQuery(query.uid, () => ({ sql, }))}
											onQueryResultChange={(result) =>
												updateQuery(query.uid, () => ({ result, }))
											}
											onQueryFieldsChange={(fields) => updateQuery(
												query.uid,
												(current) => ({ fields, testedSql: current.sql, })
											)}
										/>
										{
											query.result && query.fields.length > 0 &&
											<QueryDisplay queryResult={query.result} queryFields={query.fields} />
										}
									</div>
								</Flex>
							</Card>
						))}
						<Button
							icon={<PlusOutlined />}
							onClick={() => setQueries((prev) => [ ...prev, newDraft(prev.length + 1), ])}
						>
							Adicionar consulta
						</Button>
					</Flex>,
				},
				{
					title: 'Cruzamentos e relatório (opcional)',
					disabled: currentStep < 1,
					description: <Flex vertical gap={12}>
						<StepSubtitle>
							Cruze as consultas e monte o relatório em anexo com gráficos e tabelas.
						</StepSubtitle>
						{
							currentStep >= 1 && <>
								<hr />
								<ReportBlocksEditor
									sources={sources}
									joins={joins}
									onJoinsChange={setJoins}
									blocks={blocks}
									onBlocksChange={setBlocks}
									preview={{ title: name || 'Grupo de consultas', data: previewData, }}
								/>
							</>
						}
					</Flex>,
				},
				{
					title: 'Configurações',
					disabled: currentStep < 2,
					description: <Flex vertical gap={12}>
						<StepSubtitle>
							Mensagem, agendamento e grupos que recebem o envio.
						</StepSubtitle>
						{
							currentStep >= 2 && <>
								<hr />
								<PreviewMessageInput
									defaultMessage={customPreviewMessage}
									queryFields={[]}
									reportQueries={messageSources}
									onMessageChange={(value) => setCustomPreviewMessage(value)}
								/>
								<FormItemInput
									value={name}
									required
									label='Nome:'
									placeholder='Nome do grupo de consultas'
									onInputChange={(value) => {
										if (typeof value === 'string') setName(value);
									}}
								/>
								<FormItemInput
									required
									readonly={!!id}
									value={shortName}
									label='Nome abreviado/simplificado:'
									placeholder='Nome do simplificado'
									onInputChange={(value) => {
										if (typeof value === 'string') {
											if (value.length > 30) {
												toast.warn('O nome abreviado deve conter no máximo 30 caracteres!');
											}
											setShortName(value.substring(0, 30));
										}
									}}
									tooltip='Usado no nome do arquivo do relatório. (max: 30char)'
								/>
								<PeriodicitySelect
									defaultPeriodicity={periodicity}
									onPeriodicityChange={(value) => setPeriodicity(value)}
								/>
								<WhatsAppGroupsSelect
									defaultWhatsappGroup={defaultWhatsappGroups}
									onWhatsappGroupChange={(value) => setWhatsappGroups(value)}
								/>
								<TagsSelect
									defaultTags={tags}
									onTagsChange={(value) => setTags(value)}
								/>
								<hr />
								<Checkbox
									checked={isActive}
									onChange={(e) => setIsActive(e.target.checked)}
								>
									Grupo de consultas ativo
								</Checkbox>
							</>
						}
					</Flex>,
				},
			]}
		/>
		<Flex justify='end'>
			<Button
				loading={postObserverResult.isLoading || patchObserverResult.isLoading}
				type='primary'
				onClick={handleNextButton}
			>
				{currentStep === 2 ? 'Salvar' : 'Avançar'}
			</Button>
		</Flex>
	</DashboardTemplate>;
};
